import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Telescope, Lock } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = "BSCA Admin · Login"; }, []);

  if (user && user !== false) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/admin/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Login failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bsca-dark bsca-cosmic bsca-grain flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-md bsca-glass border border-white/10 p-8 md:p-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10">
            <Telescope className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.4} />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-white text-lg">BSCA Admin</div>
            <div className="bsca-eyebrow text-white/50 text-[10px]">Science + Consciousness Alliance</div>
          </div>
        </div>
        <h1 className="font-serif text-white text-3xl">Sign in</h1>
        <p className="text-white/60 text-sm mt-2 flex items-center gap-2"><Lock className="h-3 w-3" /> Authorized personnel only.</p>
        <div className="mt-6 space-y-5">
          <label className="block">
            <span className="bsca-eyebrow text-white/55 text-[10px]">Email</span>
            <input
              data-testid="login-email"
              required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full bg-transparent border-b border-white/20 px-0 py-3 focus:outline-none focus:border-[#D4AF37] text-white placeholder-white/40"
              placeholder="admin@bscalliance.org"
              autoComplete="username"
            />
          </label>
          <label className="block">
            <span className="bsca-eyebrow text-white/55 text-[10px]">Password</span>
            <input
              data-testid="login-password"
              required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full bg-transparent border-b border-white/20 px-0 py-3 focus:outline-none focus:border-[#D4AF37] text-white placeholder-white/40"
              autoComplete="current-password"
            />
          </label>
          {err && (
            <div data-testid="login-error" role="alert" className="error text-sm rounded-sm border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3">
              {err}
            </div>
          )}
          <button data-testid="btn-admin-login" disabled={busy} type="submit" className="w-full bsca-lift bg-[#D4AF37] text-[#050B14] font-medium py-3.5 rounded-sm disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}
